// Shared helpers for the Last-Mile shipping-costs admin (Zonas + Xdock tabs).

export const CURRENCY_BY_COUNTRY = { MX: 'MXN', BR: 'BRL' }
export const currencyFor = country => CURRENCY_BY_COUNTRY[country] || ''

// Sort key for a tier's weight ceiling. A tier with NO ceiling (null / undefined / '')
// is the open-ended TOP band ("más de X → sin límite"), so it must sort LAST — treat a
// missing ceiling as +Infinity, NOT 0 (which would wrongly float it to the first row and
// leave the "Sin límite" range at the top with a duplicated "Más de 0 kg").
export const weightSortKey = v => (v === null || v === undefined || v === '' ? Infinity : Number(v))

// The DESDE bound of a weight tier: the highest ENABLED, already-filled ceiling strictly below
// this tier's own ceiling. A blank/unset ceiling means "no ceiling yet", NOT 0 — otherwise a
// freshly added row would inject a phantom 0 into every other tier's band. A tier with no ceiling
// yet (the row just added via "add range") is the new open-ended TOP band, so it starts above the
// current highest ceiling and reads "más de <highest>", not "más de 0". Returns null when there is
// no lower ceiling (the base band, rendered as "Más de 0 kg").
export const tierRangeFrom = (rows, max) => {
  const ceilings = (rows || [])
    .filter(r => r.enabled !== false && String(r.max).trim() !== '')
    .map(r => Number(r.max))
    .filter(Number.isFinite)
  const n = Number(max)
  if (String(max).trim() === '' || !Number.isFinite(n)) {
    return ceilings.length ? Math.max(...ceilings) : null
  }
  const smaller = ceilings.filter(m => m < n)
  return smaller.length ? Math.max(...smaller) : null
}

// A cost must be at least one cent (0.01) with NO rounding leniency: a sub-cent value such as
// 0.0009 or 0.005 is rejected outright rather than rounded UP to a cent (0.005 → 0.01), which
// would let a not-really-a-price value through. The money column stores 2 decimals, so 0.01 is
// the true minimum the form should accept.
export const costIsPositiveInCents = v => {
  const n = Number(v)
  return Number.isFinite(n) && n >= 0.01
}

// Zones the backend exempts from cost/xdock gating (mirrors
// ShippingCoverage::TARIF_ZONE_NA / TARIF_ZONE_EXTENDED and the service's
// skip_cost_check?/skip_xdock_check?). A cost is never required for NONE_APPLIES,
// and an xdock binding is never required for either — forcing one would write a
// CubboLastmileXdockZone the runtime can never match (permanent routing junk).
// Named so callers can test for one of them without re-inlining the literal: moving
// coverages onto NONE_APPLIES drops the tariff join key (and, on Cubbo, the routing key),
// which is worth warning about on its own — the pair below only says "exempt from gating".
export const TARIF_ZONE_NA = 'NONE_APPLIES'
export const TARIF_ZONE_EXTENDED = 'EXTENDED_ZONE'
export const SPECIAL_TARIF_ZONES = new Set([TARIF_ZONE_NA, TARIF_ZONE_EXTENDED])
export const isSpecialZone = zone => SPECIAL_TARIF_ZONES.has(zone)

// The cross-dock currently bound to a zone (xdocks carry their bound `zone_names`),
// or null. Used to preselect the xdock across the three cost surfaces so an already
// bound zone shows its binding instead of an empty select.
export const zoneBoundXdockId = (xdocks, zone) => {
  const x = (xdocks || []).find(xd => (xd.zone_names || []).includes(zone))
  return x ? x.id : null
}

// Runtime-breaking gap codes the backend already computes per zone (see
// ShippingCosts::TarifZonesStatusService#missing). Consuming `z.missing` — instead of
// re-deriving the rule in the UI — is what keeps NONE_APPLIES / EXTENDED_ZONE and
// mapped-but-unroutable zones out of the "needs action" count, so the banner can reach 0.
export const MISSING_COST = 'MISSING_COST'
export const MISSING_XDOCK_ZONE = 'MISSING_XDOCK_ZONE'
export const MISSING_XDOCK_ROUTE = 'MISSING_XDOCK_ROUTE'
// A cheaper disabled tier shadows another band's runtime lookup — a cost-side gap, so it
// counts in the "sin costo" bucket. Without this the zone lands in the banner total but in
// neither breakdown bucket, making "N sin costo · M sin xdock" sum to less than the total.
export const COST_SHADOWED_BY_DISABLED_TIER = 'COST_SHADOWED_BY_DISABLED_TIER'

export const zoneMissingCost = z => (z.missing || []).some(m => m === MISSING_COST || m === COST_SHADOWED_BY_DISABLED_TIER)
export const zoneMissingXdock = z => (z.missing || []).some(m => m === MISSING_XDOCK_ZONE || m === MISSING_XDOCK_ROUTE)
export const zoneNeedsAction = z => (z.missing || []).length > 0
